
import Vue from 'vue';
import gql from 'graphql-tag';
import CategoryReportBarChart from './CategoryReportBarChart.vue';
import CategoryReportList from './CategoryReportList.vue';
import CategoryReportListClickable from './CategoryReportListClickable.vue';
import Tooltip from '~shared/components/generic/Tooltip.vue';
import QueryParamButtonGroup from '~shared/components/selectors/QueryParamButtonGroup.vue';
import { normalizeDateRange } from '~shared/utils';

export default Vue.extend({
  components: {
    CategoryReportBarChart,
    CategoryReportList,
    CategoryReportListClickable,
    Tooltip,
    QueryParamButtonGroup,
  },
  props: {
    includeArchivedCampaigns: {
      type: Boolean,
      default: false,
    },
    isNoData: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      startDate: '',
      endDate: '',
      tier1Data: [],
      layout: 'list',
      tier1Style: {
        height: '100px',
      },
      loading: false,
    };
  },
  computed: {
    campaignId(): string | null {
      const selectedCampaign =
        this.$store.state['advertiser-insights'].filters.selectedCampaign;
      if (!selectedCampaign || !selectedCampaign.id) {
        return null;
      }
      return selectedCampaign.id;
    },
    selectedDateRange(): Date[] {
      return this.$store.state['advertiser-insights'].filters.selectedDateRange;
    },
    groupId(): string | null {
      const group =
         this.$store.state['advertiser-insights'].filters.selectedGroup;
      if (group && group.id) {
        return group.id;
      }
      return null;
    },
  },
  watch: {
    selectedDateRange: {
      handler(val): void {
        if (val.length === 2) {
          const { startDate, endDate } = normalizeDateRange({
            startDate: val[0],
            endDate: val[1],
          });

          this.startDate = startDate.toISOString();
          this.endDate = endDate.toISOString();
        }
      },
      deep: true,
      immediate: true,
    },
    tier1Data: {
      handler(val): void {
        this.$emit('update:isNoData', this.loading || !val.length);
      },
      deep: true,
      immediate: true,
    },
    loading: {
      handler(val): void {
        this.$emit('update:isNoData', val || !this.tier1Data.length);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    setLayout(val): void {
      this.layout = val;
    },
    sortImpressions(a, b): number {
      if (a.impressions > b.impressions) {
        return -1;
      } else if (a.impressions < b.impressions) {
        return 1;
      }
      return 0;
    },
  },
  apollo: {
    stats: {
      query: gql`
        query (
          $campaignId: ID!
          $startDate: DateTime!
          $endDate: DateTime!
          $tiers: [Int]
          $groupIds: [ID!]
          $includeArchivedCampaigns: Boolean
        ) {
          CampaignCategoryStatistics(
            campaignId: $campaignId
            startDate: $startDate
            endDate: $endDate
            tiers: $tiers
            groupIds: $groupIds
            includeArchivedCampaigns: $includeArchivedCampaigns
          ) {
            statistics {
              clicks
              impressions
              ctr
              slug
              tier
              name
              children {
                clicks
                impressions
                ctr
                slug
                parentSlug
                tier
                name
              }
            }
          }
        }
      `,
      watchLoading(isLoading) {
        this.loading = isLoading;
      },
      variables(): any {
        return {
          campaignId: this.campaignId,
          startDate: this.startDate,
          endDate: this.endDate,
          groupIds: [this.groupId],
          tiers: 1,
          includeArchivedCampaigns: this.includeArchivedCampaigns,
        };
      },
      skip(): boolean {
        return (
          !this.campaignId || !this.startDate || !this.endDate || !this.groupId
        );
      },
      debounce: 200,
      notifyOnNetworkStatusChange: true,
      update(data): void {
        if (data.CampaignCategoryStatistics) {
          const s = data.CampaignCategoryStatistics.statistics;
          const result = s.sort(this.sortImpressions);

          this.tier1Data = result;
          const HEIGHT_PER_ROW = 50;
          // Set graph card heights so the bar charts will adapt correctly
          this.tier1Style.height = `${
            100 + this.tier1Data.length * HEIGHT_PER_ROW
          }px`;
        }
      },
    },
  },
});
